export const MAX_EVENT_FLYER_BYTES = 5 * 1024 * 1024;

export const EVENT_FLYER_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
];

const EVENT_MEDIA_FIELDS = [
  "flyerUrl",
  "flyerURL",
  "imageUrl",
  "imageURL",
  "posterUrl",
  "featuredImage",
  "thumbnail",
  "fileUrl",
  "image",
  "flyer",
  "poster",
  "media",
  "attachment",
  "attachments",
];

const NESTED_URL_FIELDS = [
  "url",
  "downloadUrl",
  "downloadURL",
  "flyerUrl",
  "fileUrl",
  "imageUrl",
  "src",
];

function firstUrl(value) {
  if (typeof value === "string") {
    const candidate = value.trim();
    if (!candidate) return "";

    try {
      const parsed = new URL(candidate);
      return ["http:", "https:"].includes(parsed.protocol) ? candidate : "";
    } catch {
      return "";
    }
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const url = firstUrl(item);
      if (url) return url;
    }
    return "";
  }

  if (value && typeof value === "object") {
    for (const field of NESTED_URL_FIELDS) {
      const url = firstUrl(value[field]);
      if (url) return url;
    }
  }

  return "";
}

export function resolveEventMedia(event = {}) {
  for (const field of EVENT_MEDIA_FIELDS) {
    const url = firstUrl(event[field]);
    if (url) return { url, sourceField: field };
  }

  return { url: "", sourceField: "" };
}

export function getEventFlyerUrl(event = {}) {
  return resolveEventMedia(event).url;
}

export function getEventFlyerContentType(event = {}) {
  const directType =
    event.flyerContentType || event.fileType || event.contentType || event.mimeType;
  if (typeof directType === "string" && directType.trim()) {
    return directType.trim().toLowerCase();
  }

  for (const field of EVENT_MEDIA_FIELDS) {
    const value = event[field];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nestedType = value.contentType || value.mimeType || value.type;
      if (typeof nestedType === "string" && nestedType.includes("/")) {
        return nestedType.toLowerCase();
      }
    }
  }

  return "";
}

function decodedUrl(url) {
  try {
    return decodeURIComponent(url).toLowerCase();
  } catch {
    return String(url || "").toLowerCase();
  }
}

export function getEventMediaKind(event = {}) {
  const { url, sourceField } = resolveEventMedia(event);
  const storagePath = getEventFlyerStoragePath(event);
  if (!url && !storagePath) return "none";

  const contentType = getEventFlyerContentType(event);
  if (contentType.startsWith("image/")) return "image";
  if (contentType === "application/pdf") return "document";

  const lowerUrl = decodedUrl(url || storagePath);
  if (/\.(jpe?g|png|webp)(?:[?#]|$)/.test(lowerUrl)) return "image";
  if (/\.pdf(?:[?#]|$)/.test(lowerUrl)) return "document";

  return [
    "flyerUrl",
    "flyerURL",
    "imageUrl",
    "imageURL",
    "posterUrl",
    "featuredImage",
    "thumbnail",
    "image",
    "flyer",
    "poster",
  ].includes(sourceField)
    ? "image"
    : "document";
}

export function validateEventFlyer(file) {
  if (!file) return { valid: true, error: "" };

  if (!EVENT_FLYER_MIME_TYPES.includes(String(file.type || "").toLowerCase())) {
    return {
      valid: false,
      error: "Please choose a JPG, PNG, WEBP or PDF flyer.",
    };
  }

  if (!Number.isFinite(file.size) || file.size >= MAX_EVENT_FLYER_BYTES) {
    return {
      valid: false,
      error: "The flyer must be smaller than 5 MB.",
    };
  }

  return { valid: true, error: "" };
}

function validEventId(value) {
  const eventId = String(value || "").trim();
  return eventId && !eventId.includes("/") && ![".", ".."].includes(eventId)
    ? eventId
    : "";
}

export function getEventFlyerStoragePath(event = {}) {
  const eventId = validEventId(event.id || event.sourceId);
  const storagePath =
    typeof event.flyerStoragePath === "string"
      ? event.flyerStoragePath.trim()
      : "";
  if (!eventId || !storagePath) return "";

  const segments = storagePath.split("/");
  if (
    segments.length !== 3 ||
    segments[0] !== "event-documents" ||
    segments[1] !== eventId ||
    !segments[2]
  ) {
    return "";
  }

  return storagePath;
}

export function createEventFlyerStoragePath(
  eventId,
  fileName,
  now = Date.now(),
  nonce = ""
) {
  const canonicalEventId = validEventId(eventId);
  if (!canonicalEventId) {
    throw new Error("A valid event ID is required before preparing a flyer upload.");
  }

  const safeName = String(fileName || "flyer")
    .normalize("NFKD")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(-100) || "flyer";
  const safeNonce = String(nonce || "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, 16);

  return `event-documents/${canonicalEventId}/${now}${safeNonce ? `-${safeNonce}` : ""}-${safeName}`;
}

export const EVENT_FLYER_UPLOAD_ERROR_CODE = "event/flyer-upload-failed";

export async function submitEventWithOptionalFlyer({
  file,
  eventId,
  eventRecord,
  uploadFlyer,
  saveEvent,
  now = Date.now(),
  nonce = "",
}) {
  const validation = validateEventFlyer(file);
  if (!validation.valid) throw new Error(validation.error);

  let flyer = {
    flyerUrl: "",
    flyerFileName: "",
    flyerContentType: "",
    flyerSizeBytes: 0,
    flyerStoragePath: "",
  };

  if (file) {
    flyer = {
      flyerUrl: "",
      flyerFileName: String(file.name || "flyer"),
      flyerContentType: String(file.type || "").toLowerCase(),
      flyerSizeBytes: file.size,
      flyerStoragePath: createEventFlyerStoragePath(
        eventId,
        file.name,
        now,
        nonce
      ),
    };
  }

  const record = { ...eventRecord, ...flyer };
  await saveEvent(record);

  if (file) {
    try {
      await uploadFlyer(file, flyer);
    } catch (cause) {
      const error = new Error(
        "The event was saved, but its flyer could not be uploaded."
      );
      error.code = EVENT_FLYER_UPLOAD_ERROR_CODE;
      error.eventSaved = true;
      error.cause = cause;
      throw error;
    }
  }

  return record;
}
